import { useEffect, useState, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import api from "@/lib/api";
import { X, Download, FilePdf, File as FileIcon, MagnifyingGlassPlus, MagnifyingGlassMinus, Warning, ShareNetwork } from "@phosphor-icons/react";
import ForwardModal from "@/components/ForwardModal";

/**
 * AttachmentPreviewModal — full-screen lightbox for a single team-chat
 * attachment.
 *
 * Contract
 * --------
 *   • Clicking an image / PDF / video / audio / arbitrary file chip in
 *     the chat opens this modal. NO download is triggered on open —
 *     the user must click the explicit "Download" button.
 *   • Escape key OR click on the dark overlay OR the close button
 *     returns to the chat.
 *   • Same auth flow as the existing chip: the blob is fetched via
 *     `api.get('/team-chat/files/:path', { responseType: 'blob' })` —
 *     so nothing bypasses the existing permissions/access control.
 *   • For images already fetched by the chip's thumbnail, we accept an
 *     `initialBlobUrl` prop to skip a duplicate network round-trip.
 *
 * Rendered types
 * --------------
 *   image  → <img>, click-toggles 2× zoom
 *   pdf    → <iframe> (browser's built-in PDF viewer)
 *   video  → <video controls>
 *   audio  → <audio controls>
 *   other  → filename + type card + explicit Download button
 */
export default function AttachmentPreviewModal({ att, msg, initialBlobUrl, onClose }) {
  const isImage = att.content_type?.startsWith("image/") || att.kind === "image";
  const isPdf   = att.content_type === "application/pdf"  || att.kind === "pdf";
  const isVideo = att.content_type?.startsWith("video/")  || att.kind === "video";
  const isAudio = att.content_type?.startsWith("audio/")  || att.kind === "audio";
  const isPreviewable = isImage || isPdf || isVideo || isAudio;

  const [blobUrl, setBlobUrl] = useState(initialBlobUrl || null);
  const [loading, setLoading] = useState(isPreviewable && !initialBlobUrl);
  const [error, setError]     = useState(null);
  const [zoomed, setZoomed]   = useState(false);
  const [forwarding, setForwarding] = useState(false);
  const [forwardOk, setForwardOk]   = useState(false);
  const objectUrlRef = useRef(null);

  // Fetch the blob if we don't already have one AND the type is
  // previewable. Non-previewable files never fetch — the modal just
  // shows the metadata card until the user explicitly downloads.
  useEffect(() => {
    if (blobUrl || !isPreviewable) return;
    if (!att.path) { setError("Attachment is missing its file id"); setLoading(false); return; }
    let cancelled = false;
    setLoading(true);
    api.get(`/team-chat/files/${encodeURIComponent(att.path)}`, { responseType: "blob" })
      .then(r => {
        if (cancelled) return;
        const url = URL.createObjectURL(r.data);
        objectUrlRef.current = url;
        setBlobUrl(url);
      })
      .catch(err => {
        if (cancelled) return;
        const status = err?.response?.status;
        setError(status ? `HTTP ${status}` : (err?.message || "Preview failed"));
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => {
      cancelled = true;
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = null;
      }
    };
    // We deliberately do NOT include blobUrl in the deps — we only want
    // to run this effect once per attachment path.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [att.path, isPreviewable]);

  // Escape to close.
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const doDownload = useCallback(async (e) => {
    e?.stopPropagation();
    // Prefer the already-loaded blob URL — no second network hit and no
    // risk of a stale blob for the user.
    try {
      let url = blobUrl;
      let revoke = false;
      if (!url) {
        const r = await api.get(`/team-chat/files/${encodeURIComponent(att.path)}`, { responseType: "blob" });
        url = URL.createObjectURL(r.data);
        revoke = true;
      }
      const a = document.createElement("a");
      a.href = url;
      a.download = att.original_filename || "file";
      // Append + click + remove to ensure Firefox honours the download attr.
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      if (revoke) URL.revokeObjectURL(url);
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      alert(`Download failed${status ? ` (HTTP ${status})` : ""}${detail ? `: ${detail}` : ""}`);
    }
  }, [att.path, att.original_filename, blobUrl]);

  const stop = (e) => e.stopPropagation();

  const body = (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm"
      onClick={onClose}
      data-testid="attachment-preview-overlay"
      role="dialog"
      aria-modal="true"
      aria-label={`Preview of ${att.original_filename || "attachment"}`}
    >
      {/* Card */}
      <div
        className="relative flex flex-col max-w-[92vw] max-h-[92vh] bg-white rounded-lg shadow-2xl overflow-hidden"
        onClick={stop}
        data-testid="attachment-preview-card"
      >
        {/* Header */}
        <div className="flex items-center gap-3 px-4 py-2.5 border-b border-[var(--border)] bg-white">
          <div className="flex-1 min-w-0">
            <div className="text-[13px] font-semibold truncate" data-testid="attachment-preview-filename">
              {att.original_filename || "Attachment"}
            </div>
            <div className="text-[11px] text-[var(--text-muted)]">
              {att.content_type || "unknown"}{att.size ? ` · ${(att.size / 1024).toFixed(0)} KB` : ""}
            </div>
          </div>
          {isImage && blobUrl && (
            <button
              type="button"
              onClick={(e) => { stop(e); setZoomed(z => !z); }}
              className="p-2 rounded hover:bg-[var(--bg-sidebar)] text-[var(--text-secondary)]"
              title={zoomed ? "Zoom out" : "Zoom in"}
              data-testid="attachment-preview-zoom"
            >
              {zoomed ? <MagnifyingGlassMinus size={18} /> : <MagnifyingGlassPlus size={18} />}
            </button>
          )}
          {msg?.id && (
            <button
              type="button"
              onClick={(e) => { stop(e); setForwardOk(false); setForwarding(true); }}
              className="flex items-center gap-1.5 px-3 py-1.5 text-[12px] font-semibold border border-[var(--border)] text-[var(--text-primary)] rounded hover:bg-[var(--bg-sidebar)]"
              data-testid="attachment-preview-forward"
              title="Forward to a teammate"
            >
              <ShareNetwork size={14} /> Forward
            </button>
          )}
          <button
            type="button"
            onClick={doDownload}
            className="flex items-center gap-1.5 px-3 py-1.5 text-[12px] font-semibold bg-[#5B5FC7] text-white rounded hover:bg-[#4a4eb3]"
            data-testid="attachment-preview-download"
          >
            <Download size={14} /> Download
          </button>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded hover:bg-[var(--bg-sidebar)] text-[var(--text-secondary)]"
            title="Close (Esc)"
            data-testid="attachment-preview-close"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 min-h-[60vh] min-w-[60vw] flex items-center justify-center bg-[var(--bg-sidebar)] overflow-auto">
          {error ? (
            <div className="flex flex-col items-center gap-2 p-8 text-[13px] text-[var(--danger)]" data-testid="attachment-preview-error">
              <Warning size={28} />
              <div className="font-semibold">Preview failed</div>
              <div className="text-[11px] text-[var(--text-muted)]">{error}</div>
              <button type="button" onClick={doDownload}
                      className="mt-2 flex items-center gap-1 px-3 py-1.5 text-[12px] border border-[var(--border)] bg-white rounded hover:bg-[var(--bg-sidebar)]">
                <Download size={12} /> Download instead
              </button>
            </div>
          ) : loading && !blobUrl ? (
            <div className="text-[12px] text-[var(--text-muted)]" data-testid="attachment-preview-loading">
              Loading preview…
            </div>
          ) : isImage && blobUrl ? (
            <img
              src={blobUrl}
              alt={att.original_filename || "image"}
              onClick={(e) => { stop(e); setZoomed(z => !z); }}
              className={`select-none transition-transform duration-200 ${zoomed ? "cursor-zoom-out scale-[2]" : "cursor-zoom-in"}`}
              style={{ maxWidth: zoomed ? "none" : "88vw", maxHeight: zoomed ? "none" : "78vh", objectFit: "contain" }}
              data-testid="attachment-preview-image"
            />
          ) : isPdf && blobUrl ? (
            <iframe
              src={blobUrl}
              title={att.original_filename || "PDF preview"}
              className="w-[85vw] h-[80vh] bg-white border-0"
              data-testid="attachment-preview-pdf"
            />
          ) : isVideo && blobUrl ? (
            <video src={blobUrl} controls autoPlay={false}
                   className="max-w-[88vw] max-h-[78vh]"
                   data-testid="attachment-preview-video" />
          ) : isAudio && blobUrl ? (
            <div className="flex flex-col items-center gap-3 p-8" data-testid="attachment-preview-audio">
              <audio src={blobUrl} controls className="min-w-[320px]" />
              <div className="text-[11px] text-[var(--text-muted)]">{att.original_filename}</div>
            </div>
          ) : (
            // Unsupported file type → informational card with an explicit
            // download button. NEVER auto-downloads.
            <div className="flex flex-col items-center gap-3 p-10 max-w-[420px] text-center" data-testid="attachment-preview-unsupported">
              {isPdf ? <FilePdf size={56} className="opacity-60" /> : <FileIcon size={56} className="opacity-60" />}
              <div className="text-[13px] font-semibold">This file type can't be previewed here</div>
              <div className="text-[11px] text-[var(--text-muted)] break-all">
                {att.original_filename || "file"}
                {att.content_type ? ` · ${att.content_type}` : ""}
              </div>
              <button type="button" onClick={doDownload}
                      className="mt-2 flex items-center gap-1.5 px-3 py-1.5 text-[12px] font-semibold bg-[#5B5FC7] text-white rounded hover:bg-[#4a4eb3]"
                      data-testid="attachment-preview-unsupported-download">
                <Download size={14} /> Download to open
              </button>
            </div>
          )}
        </div>
        {forwardOk && (
          <div className="px-4 py-2 border-t border-[var(--border)] bg-green-50 text-[12px] text-green-800 flex items-center gap-2"
               data-testid="attachment-forward-success">
            <ShareNetwork size={14} weight="fill" /> Forwarded successfully.
          </div>
        )}
      </div>
    </div>
  );

  const forwardOverlay = forwarding && msg?.id ? (
    <ForwardModal
      msg={msg}
      attachment={att}
      onClose={() => setForwarding(false)}
      onDone={() => { setForwarding(false); setForwardOk(true); }}
    />
  ) : null;

  // Portal so the modal always sits above chat, menus, and the composer.
  return typeof document !== "undefined"
    ? createPortal(<>{body}{forwardOverlay}</>, document.body)
    : body;
}
